import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import FormLabel from '@mui/material/FormLabel';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import { useSnackbar } from 'notistack';
import { useMutation } from '@apollo/client';
import {
  AI_WRITE_PREVIEW,
  AI_WRITE_POST,
} from '../../helpers/graphql/aiBlogger';
import TableOfContents from 'components/Post/DynamicPostComponents/TableOfContents';
import LinearProgress from '@mui/material/LinearProgress';
import NextIcon from '@mui/icons-material/DoubleArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import BackIcon from '@mui/icons-material/ArrowBack';
import CreateIcon from '@mui/icons-material/Create';
import AiBloggerProgressCheck from './AiBloggerProgressCheck';
import CustomSnackbar from './Notifications/CustomSnackbar';
import AiBloggerCreditsPurchase from './AiBloggerCreditsPurchase';
import Confetti from 'react-confetti';
import useWindowSize from 'react-use/lib/useWindowSize';
import splitTitlesArray from 'helpers/splitTitlesArray';
import Switch from '@mui/material/Switch';

export default function AiBlogger({ status, handleRefetchCredits }) {
  const [words, setWords] = useState(900);
  const [style, setStyle] = useState('Travel Guide');
  const [travelTypes, setTravelTypes] = useState({});
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [tableOfContents, setTableOfContents] = useState([]);
  const [metaDesc, setMetaDesc] = useState();
  const [tags, setTags] = useState();
  const [gptResponse1, setGptResponse1] = useState('');
  const [draftId, setDraftId] = useState();
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [useGpt4, setUseGpt4] = useState(false);

  const { width, height } = useWindowSize();

  const handleTravelTypeChange = (type, e) => {
    setTravelTypes({ ...travelTypes, [type]: e.target.checked });
  };

  const handleWordChange = (event, newValue) => {
    if (typeof newValue === 'number') {
      setWords(newValue);
    }
  };

  const handleStyleChange = event => {
    setStyle(event.target.value);
  };

  const handleTextInput = changeThis => event => {
    changeThis(event.target.value);
  };

  const travelTypesList = [
    'Backpacker',
    'Couple',
    'Foodie',
    'Digital_Nomad',
    'Family',
    'Budget_Travel',
    'Luxury',
    'Bike',
    'Camper',
    'Photography',
    'Itinerary',
    'Roadtrip',
  ];

  let hasTravelTypes = false;
  travelTypesList.forEach(travelType => {
    if (travelTypes[travelType]) {
      hasTravelTypes = true;
    }
  });

  const { enqueueSnackbar } = useSnackbar();

  const newNotification = notification => {
    if (notification !== undefined) {
      let variant = 'success';
      if (notification.success === false) {
        variant = 'error';
      }
      enqueueSnackbar(notification.message, { variant });
    }
  };

  const [aiWritePreview] = useMutation(AI_WRITE_PREVIEW);
  const [aiWritePost] = useMutation(AI_WRITE_POST);

  const handleGoBackToNotes = () => {
    setTitle('');
    setIntro('');
    setTableOfContents([]);
    setGptResponse1('');
    setTags();
    setMetaDesc();
  };

  const handleTitleChange = isLoading => {
    setIsProcessing(isLoading);
    const titleToSet = `${
      isLoading ? 'Generating... ' : ''
    }AI Blogger – TravelFeed Dashboard`;
    if (document.title != titleToSet) {
      document.title = titleToSet;
    }
  };

  const handleGeneratePost = () => {
    if (!notes || notes.length < 50 || !hasTravelTypes) {
      setShowValidation(true);
      newNotification({
        success: false,
        message: 'Please fill out all required fields',
      });
    } else {
      handleTitleChange(true);
      aiWritePreview({
        variables: {
          aiWritePreviewInput: {
            words,
            style,
            travelTypes,
            location,
            notes,
            useGpt4,
          },
        },
      })
        .then(({ data }) => {
          if (data && data.aiWritePreview && data.aiWritePreview.success) {
            handleTitleChange(false);
            setTitle(data.aiWritePreview.title);
            setIntro(data.aiWritePreview.intro);
            setTableOfContents(data.aiWritePreview.tableOfContents);
            setTags(data.aiWritePreview.tags);
            setMetaDesc(data.aiWritePreview.metaDesc);
            setGptResponse1(data.aiWritePreview.gptResponse1);
            handleRefetchCredits().catch(err => console.log(err));
          } else {
            // TODO: If generation fails (or intro is empty), auto try again 1 more time
            handleTitleChange(false);
            if (
              data &&
              data.aiWritePreview &&
              data.aiWritePreview.message === 'Insufficient credits'
            ) {
              setShowInsufficientCredits(true);
            }
            newNotification({
              success: false,
              message:
                data && data.aiWritePreview && data.aiWritePreview.message
                  ? data && data.aiWritePreview && data.aiWritePreview.message
                  : 'Generation failed - please try again',
            });
            handleRefetchCredits().catch(err => console.log(err));
          }
        })
        .catch(err => {
          console.error(err);
          handleTitleChange(false);
          newNotification({
            success: false,
            message: 'Generation failed - please try again',
          });
        });
    }
  };

  const handleReGeneratePost = () => {
    handleGoBackToNotes();
    handleGeneratePost();
  };

  const handleGenerateBody = async () => {
    handleTitleChange(true);
    const headings = splitTitlesArray(tableOfContents, words);
    console.log(headings);
    aiWritePost({
      variables: {
        aiWritePostInput: {
          title,
          intro,
          words,
          style,
          travelTypes,
          location,
          notes,
          gptResponse1,
          headings,
          metaDesc,
          tags,
          useGpt4,
        },
      },
    })
      .then(({ data }) => {
        if (data && data.aiWritePost && data.aiWritePost.success) {
          setDraftId(data.aiWritePost.message);
        }
      })
      .catch(err => console.error(err));
  };

  return (
    <>
      {status === 'success' && (
        <div className="mb-3">
          <CustomSnackbar
            variant="success"
            message="High fives all around - your AI credits have been successfully topped up!"
          />
        </div>
      )}
      {showInsufficientCredits && (
        <div className="tw-max-w-4xl tw-backdrop-blur-md tw-absolute tw-h-full tw-z-50 -tw-ml-8 -tw-mt-6 tw-pt-8 tw-px-6">
          <AiBloggerCreditsPurchase
            isOutOfCredits
            onLinkClick={() => setShowInsufficientCredits(false)}
          />
        </div>
      )}
      <>
        {isProcessing ? (
          <>
            <LinearProgress />
            <div className="tw-text-xs tw-font-medium tw-text-center pt-2">
              {intro ? 'Writing your post...' : 'Writing the outline...'} This{' '}
              will take {intro ? 'around' : 'just'}{' '}
              {intro ? 'one minute' : 'a few seconds'}.
            </div>
            {intro && <AiBloggerProgressCheck draftId={draftId} />}
          </>
        ) : title && intro && tableOfContents && tableOfContents.length > 0 ? (
          <>
            <div className="postCardContent postcontent">
              <h1 className="-tw-mt-24">{title}</h1>
              <p>{intro}</p>
              <TableOfContents headings={tableOfContents} />
            </div>
          </>
        ) : (
          <>
            <FormControl
              component="fieldset"
              variant="standard"
              error={showValidation && !hasTravelTypes}
            >
              <FormLabel component="legend">Travel Type(s):</FormLabel>
              {showValidation && !hasTravelTypes && (
                <FormHelperText>Select at least one</FormHelperText>
              )}
              <FormGroup row>
                {travelTypesList.map(type => (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox
                        color="primary"
                        checked={!!travelTypes[type]}
                        onChange={e => handleTravelTypeChange(type, e)}
                      />
                    }
                    label={type.replace('_', ' ')}
                  />
                ))}
              </FormGroup>
            </FormControl>
            <Box>
              <InputLabel id="demo-simple-select-label">
                Words: {words}
              </InputLabel>
              <Slider
                value={words}
                min={600}
                step={100}
                max={1500}
                onChange={handleWordChange}
                valueLabelDisplay="auto"
                aria-labelledby="non-linear-slider"
              />
            </Box>
            <FormControl variant="standard" fullWidth>
              <InputLabel id="demo-simple-select-label">
                Writing Style
              </InputLabel>
              <Select
                variant="standard"
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={style}
                label="Writing Style"
                onChange={handleStyleChange}
              >
                <MenuItem value="Travel Guide">Travel Guide</MenuItem>
                <MenuItem value="Storytelling">Storytelling</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="standard" fullWidth className="tw-pt-5">
              <FormLabel component="legend">
                High quality mode provides better results, but costs 20x more
                credits and is slower
              </FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={useGpt4}
                    onChange={e => setUseGpt4(e.target.checked)}
                    color="primary"
                  />
                }
                label="High quality mode"
              />
            </FormControl>
            <div className="tw-pt-5">
              <TextField
                id="outlined-basic"
                label="Location"
                variant="outlined"
                fullWidth
                inputProps={{ maxLength: 150 }}
                value={location}
                onChange={handleTextInput(setLocation)}
              />
            </div>
            <div className="tw-py-5">
              <TextField
                autoFocus
                error={showValidation && (!notes || notes.length < 50)}
                id="outlined-basic"
                label="Notes"
                variant="outlined"
                multiline
                rows={12}
                fullWidth
                inputProps={{ maxLength: 22000 }}
                value={notes}
                onChange={handleTextInput(setNotes)}
              />
              {showValidation && (!notes || notes.length < 50) && (
                <FormControl component="fieldset" variant="standard" error>
                  <FormHelperText>
                    Notes are required - minimum 50 characters
                  </FormHelperText>
                </FormControl>
              )}
            </div>
          </>
        )}
      </>
      {isProcessing ? (
        <></>
      ) : (
        <div className="container">
          <div className="tw-flow-root">
            {title && intro && tableOfContents.length > 0 ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  className="tw-float-right"
                  onClick={handleGenerateBody}
                >
                  <CreateIcon className="mr-2" /> Generate full post
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  className="tw-float-right tw-mr-2"
                  onClick={handleReGeneratePost}
                >
                  <RefreshIcon className="mr-2" /> Try again
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  className="tw-float-right tw-mr-2"
                  onClick={handleGoBackToNotes}
                >
                  <BackIcon className="mr-2" /> Back
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                className="tw-float-right"
                onClick={handleGeneratePost}
              >
                <NextIcon className="mr-2" /> Generate Preview
              </Button>
            )}
          </div>
        </div>
      )}
      {status === 'success' && (
        <Confetti width={width} height={height} recycle={false} />
      )}
    </>
  );
}
